import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Flex } from 'antd';
import { FC, memo, useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import {
  DeleteFormTableRowButton,
  FormField,
  FormInputNumber,
  FormSelect,
  TanstackFormTable,
  getFormTableFieldName,
} from '../../common/form';
import { getDatePeriodString, getDayjs, getDayjsNow } from '../../common/utils';
import {
  DataObjectValidFormDataType,
  ExecutionPeriodFormDataType,
} from '../schema';
import translations from '../translations/PeriodDetailsTable.translations.json';
import { PredefineClassifiers } from './types';
import {
  formatPeriodicity,
  usePeriodLoadingType,
  usePeriodPredefines,
} from './utils';
import { CellContext, createColumnHelper } from '@tanstack/react-table';
import { DataObjectGroupType } from '../types';
import {
  SystemClassifierOption,
  useSystemClassifierSelectOptions,
} from '../../configuration';

const { addTranslations, useTranslation } = i18nNsFactory(
  'period-details-table'
);
addTranslations(translations);

export interface PeriodDetailsTableProps {
  groupType: DataObjectGroupType;
  isDisabled?: boolean;
}

const columnHelper = createColumnHelper<ExecutionPeriodFormDataType>();

export function PeriodEditTable({
  groupType,
  isDisabled,
}: PeriodDetailsTableProps) {
  const { t } = useTranslation();
  const { periodLoadingType, periodCount } = usePeriodLoadingType();
  const predefines = usePeriodPredefines();
  const { SOURCE_DATA_VERSION: sourceDataVersionOptions } =
    useSystemClassifierSelectOptions(['SOURCE_DATA_VERSION']);

  const renderPeriodCountPreview = useCallback(
    (value: any) => {
      const fixed = periodLoadingType === 'FIXED';
      if (fixed || periodLoadingType === 'VARIABLE') {
        return t('column.periodCount.value', {
          value: fixed ? periodCount : value,
        });
      }
      return periodLoadingType
        ? t(`column.periodCount.${periodLoadingType}`)
        : '';
    },
    [periodCount, periodLoadingType, t]
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('monthsPredefined', {
        header: t('column.periodicity.label'),
        enableColumnFilter: false,
        enableSorting: false,
        cell: (props) => (
          <MonthsPredefinedCell {...props} predefines={predefines} />
        ),
      }),
      columnHelper.accessor('validPeriod', {
        header: t('column.validPeriod.label'),
        enableColumnFilter: false,
        meta: { width: 200 },
        cell: ValidPeriodCell,
      }),
      columnHelper.accessor('periodCount', {
        header: t('column.periodCount.label'),
        enableColumnFilter: false,
        enableSorting: false,
        meta: { width: 200 },
        cell: (props) => (
          <PeriodCountCell
            {...props}
            renderPeriodCountPreview={renderPeriodCountPreview}
            suffix={t('column.periodCount.suffix')}
            periodLoadingType={periodLoadingType}
            isDisabled={isDisabled}
          />
        ),
      }),
      ...(groupType === DataObjectGroupType.O
        ? [
            columnHelper.accessor('sourceDataVersionId', {
              header: t('column.sourceDataVersionId.label'),
              enableColumnFilter: false,
              enableSorting: false,
              meta: { width: 220 },
              cell: (props) => (
                <SourceDataVersionCell
                  {...props}
                  options={sourceDataVersionOptions}
                />
              ),
            }),
          ]
        : []),
      columnHelper.accessor('operations' as any, {
        header: () => undefined,
        enableSorting: false,
        enableColumnFilter: false,
        meta: {
          width: 48,
          className: 'sticky-right',
        },
        cell: (props) => (
          <Flex justify="center" gap="small">
            <DeleteFormTableRowButton context={props} disabled={isDisabled} />
          </Flex>
        ),
      }),
    ],
    [
      groupType,
      isDisabled,
      periodLoadingType,
      predefines,
      renderPeriodCountPreview,
      sourceDataVersionOptions,
      t,
    ]
  );

  const hideOldRows = useCallback((row: ExecutionPeriodFormDataType) => {
    const validityEnd = row.validPeriod[1];
    if (!validityEnd) {
      return {};
    }

    const ignored = getDayjsNow().subtract(1, 'years').startOf('date');
    const end = getDayjs(validityEnd)?.startOf('date');
    return end && end.isSameOrBefore(ignored) ? { hidden: true } : {};
  }, []);

  return (
    <TanstackFormTable
      name="execution.executionPeriods"
      columns={columns}
      rowHtmlAttributes={hideOldRows}
      defaultSort={[{ id: 'validPeriod', desc: false }]}
      sticky
      virtual
      scrollContainerSelector="#detail-layout-main-content"
    />
  );
}

const MonthsPredefinedCell: FC<
  CellContext<ExecutionPeriodFormDataType, any> & {
    predefines: PredefineClassifiers;
  }
> = memo((props) => {
  const { getValues } = useFormContext<DataObjectValidFormDataType>();
  return (
    <FormField
      name={getFormTableFieldName(props)}
      size="small"
      preview={{
        previewOnly: true,
        render: () =>
          formatPeriodicity(
            getValues(`execution.executionPeriods.${props.row.index}`),
            props.predefines
          ),
      }}
    />
  );
});

const ValidPeriodCell: FC<CellContext<ExecutionPeriodFormDataType, any>> = memo(
  (props) => (
    <FormField
      name={getFormTableFieldName(props)}
      size="small"
      preview={{
        previewOnly: true,
        render: (value) => getDatePeriodString(value[0], value[1]),
      }}
    />
  )
);

const PeriodCountCell: FC<
  CellContext<ExecutionPeriodFormDataType, any> & {
    renderPeriodCountPreview: (value: any) => string;
    suffix?: string;
    periodLoadingType?: string;
    isDisabled?: boolean;
  }
> = memo((props) => (
  <FormField
    name={getFormTableFieldName(props)}
    size="small"
    preview={{
      previewOnly: props.isDisabled || props.periodLoadingType !== 'VARIABLE',
      render: props.renderPeriodCountPreview,
    }}
  >
    <Flex align="end">
      <PeriodCountField
        size="small"
        autoFocus
        name={getFormTableFieldName(props)}
        min={1}
        max={999999}
      />
      <span>{props.suffix}</span>
    </Flex>
  </FormField>
));

const SourceDataVersionCell: FC<
  CellContext<ExecutionPeriodFormDataType, any> & {
    options: SystemClassifierOption[];
  }
> = memo((props) => {
  const name = getFormTableFieldName(props);
  return (
    <FormField size="small" name={name}>
      <FormSelect size="small" name={name} options={props.options} />
    </FormField>
  );
});

const PeriodCountField = styled(FormInputNumber)`
  margin-right: 8px;
  max-width: 64px;
`;
